import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { snsContentGeneratorApi } from '../../../lib/api.js';

/** TikTokの認証画面からのリダイレクト結果。 @graph-connects none */
type TiktokCallbackStatus = 'processing' | 'success' | 'error';

/**
 * TikTok連携ポップアップの `window.open` の第2引数（window name）と、連携結果通知に使う
 * `BroadcastChannel` のチャンネル名を兼ねる識別子。`index.tsx` の `handleConnectTiktokAccount`
 * と同じ値を使う必要があるため、このファイルから export してそちらで import する。
 * @graph-connects none
 */
export const TIKTOK_CONNECT_WINDOW_NAME = 'tiktok-connect';

/** 元タブへ連携結果を伝える BroadcastChannel メッセージのペイロード形。 @graph-connects none */
export interface TiktokCallbackMessage {
  type: 'tiktok-callback';
  status: 'success' | 'error';
}

/**
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Marketing
 * @graph-business TikTok OAuth連携のredirect_uri先ページ。ブラウザのリダイレクトで受け取ったcode/stateをtiktokCallback procedureへ中継し、トークン交換の結果を表示する
 * @graph-connects snsContentGeneratorApi [calls] tiktokCallback oRPC procedureへの中継
 */
export const Route = createFileRoute('/sns-content-generator/tiktok/callback')({
  component: TiktokCallbackPage,
  validateSearch: (search: Record<string, unknown>) => ({
    code: typeof search.code === 'string' ? search.code : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
  }),
});

/**
 * この画面は「TikTok連携」ボタンから開いたポップアップの中で開かれる想定（ポップアップブロック時は
 * 同じタブでの遷移にフォールバックする。`index.tsx` の `handleConnectTiktokAccount` 参照）。
 * ポップアップ判定は `window.name` で行う（`window.opener` は使わない。`index.tsx` 側で
 * reverse tabnabbing 対策として意図的に `popup.opener = null` を設定しているため、ポップアップと
 * して開かれていても `window.opener` は常に `null` になる）。ポップアップと判定できた場合は、
 * 結果を `BroadcastChannel` で元タブへ通知してから自動的に閉じる。元タブ側は同じチャンネルを
 * 購読していて、通知を受けて表示を更新するため、手動リロードが不要になる。ポップアップと判定
 * できない（ポップアップブロックのフォールバックで同じタブに遷移してきた）場合は、従来どおり
 * 戻り導線を表示するだけにする。
 * @graph-connects none
 */
export function TiktokCallbackPage() {
  const { t } = useTranslation();
  const { code, state } = Route.useSearch();
  const [status, setStatus] = useState<TiktokCallbackStatus>('processing');
  // tiktokCallbackはstateを即消費する非冪等操作のため、StrictModeの開発時二重マウントで
  // 同一code/stateを2回送らないようにガードする。unmount後のsetStateはReact 18ではno-opなので
  // cancelledフラグでのガードは不要（hasRequestedRefのみで十分）。
  const hasRequestedRef = useRef(false);
  const isPopup = window.name === TIKTOK_CONNECT_WINDOW_NAME;

  useEffect(() => {
    if (!code || !state) {
      setStatus('error');
      return;
    }
    if (hasRequestedRef.current) {
      return;
    }
    hasRequestedRef.current = true;
    snsContentGeneratorApi
      .tiktokCallback({ code, state })
      .then(() => {
        setStatus('success');
      })
      .catch(() => {
        setStatus('error');
      });
  }, [code, state]);

  useEffect(() => {
    if (status === 'processing' || !isPopup) return;
    const channel = new BroadcastChannel(TIKTOK_CONNECT_WINDOW_NAME);
    const message: TiktokCallbackMessage = { type: 'tiktok-callback', status };
    channel.postMessage(message);
    channel.close();
    window.close();
  }, [status, isPopup]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="glass-card max-w-md w-full p-8 text-center space-y-4">
        {status === 'processing' && (
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('snsContentGenerator.tiktokCallbackProcessing')}
          </p>
        )}
        {status === 'success' && (
          <p className="text-sm text-[var(--color-success)]">
            {t('snsContentGenerator.tiktokCallbackSuccess')}
          </p>
        )}
        {status === 'error' && (
          <p className="text-sm text-[var(--color-error)]">
            {t('snsContentGenerator.tiktokCallbackError')}
          </p>
        )}
        {status !== 'processing' &&
          (isPopup ? (
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('snsContentGenerator.tiktokCallbackAutoClose')}
            </p>
          ) : (
            <Link
              to="/sns-content-generator"
              className="inline-block text-sm text-[var(--color-accent-primary)] hover:underline"
            >
              {t('snsContentGenerator.tiktokCallbackBackLink')}
            </Link>
          ))}
      </div>
    </div>
  );
}
